/**
 * Super-admin demo-tenant provisioning + reset page.
 *
 * Founder direction 2026-06-03 — single SA page that takes an admin email
 * and either provisions the demo tenant from scratch or wipes the existing
 * demo tenant and re-provisions it. The temp password is minted server-side
 * and emailed to the prospect.
 *
 * Typed-string confirm gate ("RESET DEMO") matches the schema-side
 * ``Literal["RESET DEMO"]`` enforced by the backend.
 *
 * After a successful reset the page surfaces the login URL the SA hands
 * to the prospect, plus a copy-to-clipboard button so the SA can paste
 * it into a chat / email without re-typing.
 */
import { useState, useEffect, type FormEvent } from "react";
import { useDemoReset } from "@/hooks/queries/admin/useDemoReset";
import { useDemoStatus } from "@/hooks/queries/admin/useDemoStatus";

const CONFIRM_TOKEN = "RESET DEMO";

export default function DemoReset(): JSX.Element {
  const status = useDemoStatus();
  const reset = useDemoReset();

  const [adminEmail, setAdminEmail] = useState("");
  const [adminFirstName, setAdminFirstName] = useState("Demo");
  const [adminLastName, setAdminLastName] = useState("Admin");
  const [confirmInput, setConfirmInput] = useState("");
  const [copyState, setCopyState] = useState<"idle" | "copied">("idle");

  // Pre-fill the email field with the currently-provisioned admin email
  // the first time status loads — saves the SA a re-type when they want
  // to re-issue credentials for the same prospect.
  useEffect(() => {
    if (status.data?.admin_email && adminEmail === "") {
      setAdminEmail(status.data.admin_email);
    }
  }, [status.data?.admin_email, adminEmail]);

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (confirmInput !== CONFIRM_TOKEN) return;
    reset.mutate({
      admin_email: adminEmail,
      admin_first_name: adminFirstName,
      admin_last_name: adminLastName,
      confirm: CONFIRM_TOKEN,
    });
  }

  async function copyLoginUrl() {
    const url = reset.data?.login_url;
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
      setTimeout(() => setCopyState("idle"), 2000);
    } catch {
      // Clipboard API can fail under HTTP origins / disabled permissions.
      // No-op fallback — the URL is visible on the page for manual select+copy.
    }
  }

  const inputClass =
    "w-full rounded border border-brand-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary";
  const labelClass =
    "mb-1 block text-sm font-medium text-text-secondary dark:text-brand-100/70";

  const verb = status.data?.exists ? "Reset" : "Provision";
  const confirmOk = confirmInput === CONFIRM_TOKEN;
  const submitEnabled =
    !reset.isPending && confirmOk && adminEmail.length > 0;

  // ── Error envelope decoding ─────────────────────────────────────────────
  let errorCard: JSX.Element | null = null;
  if (reset.isError) {
    const err = reset.error;
    const status409 = err.status === 409;
    const status500 = err.status === 500;
    // The backend's 500 body carries detail.{error,message,recovery,phase};
    // apiClient lifts ``error`` + ``message`` to err.code + err.message, so
    // we surface those plus a static "retry idempotent" hint on 500. The
    // ``phase`` field is in the structured audit row, not on the wire —
    // intentional, so we point at the SA audit log for the diagnostic.
    errorCard = (
      <div
        className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
        data-testid="demo-reset-error"
      >
        <p className="font-medium">
          {status409
            ? "Reset already in progress"
            : err.error === "demo_asset_missing"
              ? "Demo fixtures missing on server"
              : status500
                ? "Reset failed"
                : "Reset failed"}
        </p>
        <p className="mt-1">{err.message}</p>
        {status500 && err.error !== "demo_asset_missing" ? (
          <p className="mt-1 text-xs">
            Recovery: click Reset again — the operation is idempotent. Check
            the SA audit log for the failing phase.
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-5">
      <header>
        <h1 className="text-xl font-semibold text-text-primary dark:text-text-primary">
          Demo tenant
        </h1>
        <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/60">
          Provision or reset the demo tenant. Hands the prospect a perfect
          dashboard: 3 JDs, 24 ranked candidates with resumes and headshots,
          and 176 credits to play with.
        </p>
      </header>

      {/* Current state */}
      <section
        className="rounded border border-brand-200 bg-white px-4 py-3 dark:border-white/[0.08] dark:bg-surface-card"
        data-testid="demo-status-pane"
      >
        <h2 className="text-sm font-semibold text-text-primary dark:text-text-primary">
          Current state
        </h2>
        {status.isLoading ? (
          <p className="mt-1 text-sm text-text-secondary">Loading…</p>
        ) : status.data?.exists ? (
          <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            <dt className="text-text-secondary dark:text-brand-100/60">
              Admin email
            </dt>
            <dd className="dark:text-text-primary">
              {status.data.admin_email ?? "—"}
            </dd>
            <dt className="text-text-secondary dark:text-brand-100/60">
              Candidates
            </dt>
            <dd className="dark:text-text-primary">
              {status.data.candidate_count}
            </dd>
            <dt className="text-text-secondary dark:text-brand-100/60">
              Rankings
            </dt>
            <dd className="dark:text-text-primary">
              {status.data.ranking_count}
            </dd>
            <dt className="text-text-secondary dark:text-brand-100/60">
              Last reset
            </dt>
            <dd className="dark:text-text-primary">
              {status.data.last_reset_at
                ? new Date(status.data.last_reset_at).toLocaleString()
                : "—"}
            </dd>
          </dl>
        ) : (
          <p className="mt-1 text-sm text-text-secondary dark:text-brand-100/60">
            No demo tenant provisioned. Fill the form below to create one.
          </p>
        )}
      </section>

      {/* Success card */}
      {reset.data ? (
        <section
          className="rounded border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200"
          data-testid="demo-reset-success"
        >
          <p className="font-semibold">Demo tenant ready.</p>
          <p className="mt-1">
            Seeded {reset.data.jds_seeded} JDs, {reset.data.candidates_seeded}{" "}
            candidates, {reset.data.rankings_seeded} rankings.{" "}
            {reset.data.initial_credits} credits granted.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <code
              className="rounded bg-white/70 px-2 py-1 text-xs dark:bg-black/30"
              data-testid="demo-login-url"
            >
              {reset.data.login_url}
            </code>
            <button
              type="button"
              onClick={copyLoginUrl}
              className="rounded border border-emerald-400 bg-white px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/40 dark:bg-emerald-900/30 dark:text-emerald-200"
            >
              {copyState === "copied" ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="mt-2 text-xs">
            Hand the prospect the URL above + the admin email. Their temp
            password is emailed to them automatically.
          </p>
        </section>
      ) : null}

      {/* Form */}
      <form
        onSubmit={onSubmit}
        className="space-y-3"
        data-testid="demo-reset-form"
      >
        <div>
          <label className={labelClass}>Admin email</label>
          <input
            required
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            className={inputClass}
            data-testid="demo-admin-email"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelClass}>Admin first name</label>
            <input
              required
              value={adminFirstName}
              onChange={(e) => setAdminFirstName(e.target.value)}
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Admin last name</label>
            <input
              required
              value={adminLastName}
              onChange={(e) => setAdminLastName(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>
        <div>
          <label className={labelClass}>
            Type{" "}
            <code className="rounded bg-brand-100 px-1 dark:bg-white/10">
              {CONFIRM_TOKEN}
            </code>{" "}
            to confirm
          </label>
          <input
            required
            value={confirmInput}
            onChange={(e) => setConfirmInput(e.target.value)}
            placeholder={CONFIRM_TOKEN}
            className={inputClass}
            data-testid="demo-confirm-input"
          />
        </div>

        {errorCard}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!submitEnabled}
            className="rounded bg-accent-primary-hover px-4 py-2 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
            data-testid="demo-submit"
          >
            {reset.isPending
              ? `${verb}ing…`
              : status.data?.exists
                ? "Reset demo tenant"
                : "Provision demo tenant"}
          </button>
        </div>
        {reset.isPending ? (
          <p className="text-xs text-text-secondary dark:text-brand-100/60">
            Takes about a minute — the AI ranks each of the 24 candidates
            against its JD synchronously.
          </p>
        ) : null}
      </form>
    </div>
  );
}
